import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { mark, deEmphasis, chrome } from '../../../analytics/vizTokens';

// Line chart with a crosshair + tooltip. An SVG chart is interactive by nature,
// so the hover layer ships by default rather than as an enhancement.
//
// Two series max here (current and prior year). Both are named in the legend, so
// identity never rests on colour alone -- the prior-year line is also dashed.
function TrendChart({
  points,
  xKey = 'businessDate',
  yKey = 'netSalesAmount',
  compareKey = null,
  formatValue = (v) => v,
  seriesLabel,
  compareLabel,
  height = 220,
}) {
  const [hover, setHover] = useState(null);

  const geom = useMemo(() => {
    const width = 800;
    const padL = 8;
    const padR = 8;
    const padT = 10;
    const padB = 22;
    const values = points.map((p) => Number(p[yKey]) || 0);
    const compares = compareKey
      ? points.map((p) => Number(p[compareKey]) || 0)
      : [];
    const max = Math.max(...values, ...compares, 0) * 1.08 || 1;
    const stepX = points.length > 1 ? (width - padL - padR) / (points.length - 1) : 0;
    const toX = (i) => padL + i * stepX;
    const toY = (v) => padT + (1 - v / max) * (height - padT - padB);
    const path = (arr) =>
      arr.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
    return { width, padL, padB, padT, max, toX, toY, path, values, compares, height };
  }, [points, yKey, compareKey, height]);

  if (!points.length) {
    return <div className="py-8 text-center text-sm text-muted">—</div>;
  }

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const i = Math.round(ratio * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };

  const hovered = hover != null ? points[hover] : null;

  return (
    <div>
      {/* Legend is always present for two series. */}
      {compareKey && (
        <div className="mb-2 flex items-center gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded-full" style={{ backgroundColor: mark }} />
            {seriesLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-0 w-4 border-t-2 border-dashed"
              style={{ borderColor: deEmphasis }}
            />
            {compareLabel}
          </span>
        </div>
      )}

      <div className="flex">
        {/* Recessive y-axis. The gridlines alone left the magnitude unreadable
            without hovering. padT/padB are px because the viewBox height equals
            the rendered height. */}
        <div
          className="mr-2 flex w-20 shrink-0 flex-col justify-between text-right text-[10px] leading-none text-muted tabular-nums"
          style={{ height, paddingTop: geom.padT - 4, paddingBottom: geom.padB - 4 }}
          aria-hidden="true"
        >
          <span>{formatValue(geom.max)}</span>
          <span>{formatValue(geom.max / 2)}</span>
          <span>{formatValue(0)}</span>
        </div>
        <div className="relative flex-1">
        <svg
          viewBox={`0 0 ${geom.width} ${height}`}
          width="100%"
          height={height}
          preserveAspectRatio="none"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          role="img"
        >
          {/* Recessive gridlines. */}
          {[0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={f}
              x1={geom.padL}
              x2={geom.width - 8}
              y1={geom.toY(geom.max * f)}
              y2={geom.toY(geom.max * f)}
              stroke={chrome.grid}
              strokeWidth="1"
            />
          ))}
          <line
            x1={geom.padL}
            x2={geom.width - 8}
            y1={geom.toY(0)}
            y2={geom.toY(0)}
            stroke={chrome.axis}
            strokeWidth="1"
          />

          {compareKey && (
            <path
              d={geom.path(geom.compares)}
              fill="none"
              stroke={deEmphasis}
              strokeWidth="2"
              strokeDasharray="5 4"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <path
            d={geom.path(geom.values)}
            fill="none"
            stroke={mark}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          {hover != null && (
            <>
              <line
                x1={geom.toX(hover)}
                x2={geom.toX(hover)}
                y1={geom.padT}
                y2={geom.toY(0)}
                stroke={chrome.axis}
                strokeWidth="1"
              />
              {/* 2px surface ring keeps the marker legible over the line. */}
              <circle
                cx={geom.toX(hover)}
                cy={geom.toY(geom.values[hover])}
                r="5"
                fill={mark}
                stroke={chrome.surface}
                strokeWidth="2"
              />
            </>
          )}
        </svg>

        {hovered && (
          <div
            className="pointer-events-none absolute top-0 z-10 rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop"
            style={{
              left: `${(hover / Math.max(points.length - 1, 1)) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="font-semibold text-ink">
              {dayjs(hovered[xKey]).format('ddd DD MMM')}
            </div>
            <div className="mt-1 flex justify-between gap-4 tabular-nums">
              <span className="text-muted">{seriesLabel}</span>
              <span className="font-semibold text-ink">{formatValue(hovered[yKey])}</span>
            </div>
            {compareKey && (
              <div className="flex justify-between gap-4 tabular-nums">
                <span className="text-muted">{compareLabel}</span>
                <span className="text-ink">{formatValue(hovered[compareKey])}</span>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      <div className="ml-22 mt-1 flex justify-between text-xs text-muted tabular-nums" style={{ marginLeft: '5.5rem' }}>
        <span>{dayjs(points[0][xKey]).format('DD MMM')}</span>
        <span>{dayjs(points[points.length - 1][xKey]).format('DD MMM')}</span>
      </div>
    </div>
  );
}

export default TrendChart;
